import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useRbac } from '../../context';
import { toast } from 'react-hot-toast';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import { atsAnalysis } from '../../services/usersapi';

// Configure PDF.js worker
try {
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
} catch (error) {
  console.warn('PDF.js worker configuration failed:', error);
  // Fallback: use a basic worker source
  pdfjsLib.GlobalWorkerOptions.workerSrc = '';
}

const AtsChecker: React.FC = () => {
  const { user } = useRbac() as { user: any | null };
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [jobDescriptionFile, setJobDescriptionFile] = useState<File | null>(null);
  const [jobDescriptionText, setJobDescriptionText] = useState('');
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [resumePreview, setResumePreview] = useState<string | null>(null);
  const [jobDescriptionPreview, setJobDescriptionPreview] = useState<string | null>(null);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isParsingJobDescription, setIsParsingJobDescription] = useState(false);
  const [parsingProgress, setParsingProgress] = useState(0);
  const [isDragOverResume, setIsDragOverResume] = useState(false);
  
  const resumeInputRef = useRef<HTMLInputElement>(null);
  const jobInputRef = useRef<HTMLInputElement>(null);

  // Drag and drop handlers for resume
  const handleResumeDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverResume(true);
  };

  const handleResumeDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverResume(false);
  };

  const handleResumeDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverResume(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      processResumeFile(file);
    }
  };

  const processResumeFile = (file: File) => {
    // Validate file type
    if (!(file.type === 'application/pdf' || file.type === 'application/msword' || 
          file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
      toast.error('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error('File size too large. Please upload a file smaller than 2MB.');
      return;
    }

    setResumeFile(file);
    setResumePreview(null);
    setAnalysisResult(null);
    
    // Try to parse the file for preview
    if (file.type === 'application/pdf') {
      parsePDFForPreview(file, setResumePreview);
    } else {
      parseWordForPreview(file, setResumePreview);
    }
  };

  const handleResumeUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    processResumeFile(file);
  };

  const handleJobDescriptionUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!(file.type === 'application/pdf' || file.type === 'application/msword' || 
          file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
      toast.error('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error('File size too large. Please upload a file smaller than 2MB.');
      return;
    }

    setJobDescriptionFile(file);
    setJobDescriptionPreview(null);
    setJobDescriptionText('');
    setAnalysisResult(null);

    // Try to parse the file for preview and text extraction
    setIsParsingJobDescription(true);
    setParsingProgress(0);
    
     if (file.type === 'application/pdf') {
       parsePDFForText(file, setJobDescriptionText, setJobDescriptionPreview, setIsParsingJobDescription, setParsingProgress);
     } else {
       parseWordForText(file, setJobDescriptionText, setJobDescriptionPreview, setIsParsingJobDescription, setParsingProgress);
     }
  };

  const parsePDFForPreview = async (file: File, setPreview: (preview: string | null) => void) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(1);
      
      const viewport = page.getViewport({ scale: 0.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      if (!context) {
        console.error('Could not get canvas context');
        return;
      }
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      await page.render({
        canvasContext: context,
        viewport: viewport,
        canvas: canvas
        }).promise;
        
      const dataUrl = canvas.toDataURL();
      setPreview(dataUrl);
    } catch (error) {
      console.error('Error parsing PDF for preview:', error);
      // If parsing fails, we'll show the file as uploaded but not parsed
    }
  };

  const parsePDFForText = async (
    file: File, 
    setText: (text: string) => void, 
    setPreview: (preview: string | null) => void,
    setIsParsing: (parsing: boolean) => void,
    setProgress: (progress: number) => void
  ) => {
    try {
      setProgress(10);
      const arrayBuffer = await file.arrayBuffer();
      setProgress(30);
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      setProgress(50);
      
      let fullText = '';
      const numPages = pdf.numPages;
      
      for (let i = 1; i <= Math.min(numPages, 3); i++) { // Limit to first 3 pages
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item: any) => item.str).join(' ');
        fullText += pageText + '\n';
        setProgress(50 + (i / Math.min(numPages, 3)) * 40);
      }
      
      setProgress(100);
      
      if (fullText.trim().length === 0) {
        // No text extracted - show preview instead
        console.log('No readable text found in PDF. Showing preview instead.');
        setPreview('image-based-pdf');
        setText('');
      } else {
        // Text extracted successfully - show text area
        setText(fullText.trim());
        setPreview(null);
      }
      
      setIsParsing(false);
    } catch (error) {
      console.error('Error parsing PDF for text:', error);
      // If parsing fails completely, show preview
      setPreview('image-based-pdf');
      setText('');
      setIsParsing(false);
      setProgress(0);
    }
  };

  const parseWordForPreview = async (file: File, setPreview: (preview: string | null) => void) => {
    try {
      // For Word documents, we'll just show a document icon
      // In a real implementation, you might want to convert to HTML or use a different approach
      setPreview('word-document');
    } catch (error) {
      console.error('Error parsing Word for preview:', error);
    }
  };

  const parseWordForText = async (
    file: File, 
    setText: (text: string) => void, 
    setPreview: (preview: string | null) => void,
    setIsParsing: (parsing: boolean) => void,
    setProgress: (progress: number) => void
  ) => {
    try {
      setProgress(20);
      const arrayBuffer = await file.arrayBuffer();
      setProgress(40);
      const result = await mammoth.extractRawText({ arrayBuffer });
      setProgress(80);
      
      const extractedText = result.value.trim();
      
      if (extractedText.length === 0) {
        // No text extracted - show preview instead
        console.log('No readable text found in Word document. Showing preview instead.');
        setPreview('word-document');
        setText('');
      } else {
        // Text extracted successfully - show text area
        setText(extractedText);
        setPreview(null);
      }
      
      setProgress(100);
      setIsParsing(false);
    } catch (error) {
      console.error('Error parsing Word for text:', error);
      // If parsing fails completely, show preview
      setPreview('word-document');
      setText('');
      setIsParsing(false);
      setProgress(0);
    }
  };

  const handleRunAnalysis = async () => {
    if (!resumeFile) {
      toast.error('Please upload a resume first');
      return;
    }
    
     // Check if we have job description content
     // If there's a preview, it means parsing failed, so we need the file
     // If there's no preview, we need text content
     if (jobDescriptionPreview && !jobDescriptionFile) {
       toast.error('Job description file is required');
       return;
     }
     if (!jobDescriptionPreview && !jobDescriptionText.trim()) {
       toast.error('Please provide a job description (either as text or upload a file)');
       return;
     }

    setIsAnalyzing(true);
    setParsingProgress(0);
    
    try {
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setParsingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 12;
        });
      }, 400);
      
       // Determine what to send for job description
       // If there's a preview, it means parsing failed, so send the file to backend for processing
       // If there's no preview but we have text, send the text
       const jobDescriptionToSend = jobDescriptionPreview ? jobDescriptionFile! : jobDescriptionText;
     
      console.log('Sending analysis request:', {
        resumeFileName: resumeFile.name,
        resumeFileType: resumeFile.type,
        resumeFileSize: resumeFile.size,
        jobDescriptionType: jobDescriptionPreview ? 'file (parsing failed)' : 'text (parsing succeeded)',
        jobDescriptionContent: jobDescriptionPreview ? 
          `File: ${jobDescriptionFile?.name} (${jobDescriptionFile?.size} bytes) - will be processed by backend` :
          `Text (${jobDescriptionText.length} characters) - extracted from frontend`
      });
      
        // Send to backend for processing
      const result = await atsAnalysis(resumeFile, jobDescriptionToSend);
      
      clearInterval(progressInterval);
      setParsingProgress(100);
      
      setTimeout(() => {
        setAnalysisResult(result);
        setIsAnalyzing(false);
        setParsingProgress(0);
        toast.success('Analysis completed successfully!');
      }, 500);
      
    } catch (error) {
      console.error('Error during analysis:', error);
      toast.error('Analysis failed. Please try again.');
      setIsAnalyzing(false);
      setParsingProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-8 lg:mb-12">
          <div className="flex items-center justify-center gap-4 mb-6">
            {/* <div className="p-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div> */}
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                  ATS Checker
                </h1>
              <p className="text-sm sm:text-base lg:text-lg text-gray-600 mt-2 max-w-2xl mx-auto">
                  Analyze resume compatibility against job descriptions
                </p>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-8">
          {/* Resume Upload */}
          <Card className="shadow-lg border-0 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <CardHeader className="bg-gray-100 px-4 sm:px-6 py-4 sm:py-5">
              <CardTitle className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: '#EE7D63' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span>Upload Resume</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-4">
                <div 
                  className={`border-2 border-dashed rounded-xl p-4 sm:p-6 transition-all duration-300 cursor-pointer group min-h-[240px] flex items-center justify-center ${
                    isDragOverResume 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50'
                  }`}
                  onClick={() => resumeInputRef.current?.click()}
                  onDragOver={handleResumeDragOver}
                  onDragLeave={handleResumeDragLeave}
                  onDrop={handleResumeDrop}
                >
                  <input
                    ref={resumeInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleResumeUpload}
                    className="hidden"
                  />
                  
                  {resumeFile ? (
                    // Show compact preview when file is uploaded
                    <div className="flex items-center justify-center w-full">
                      <div className="flex items-center justify-between w-full max-w-md">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          {resumePreview && resumeFile.type === 'application/pdf' ? (
                            <div 
                              className="relative w-16 h-20 border border-gray-200 rounded overflow-hidden cursor-pointer hover:border-blue-400 hover:shadow-md transition-all duration-200 group"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (resumeFile) {
                                  try {
                                    const fileUrl = URL.createObjectURL(resumeFile);
                                    const newWindow = window.open(fileUrl, '_blank');
                                    if (!newWindow) {
                                      toast.error('Please allow popups to view the file');
                                    }
                                    setTimeout(() => URL.revokeObjectURL(fileUrl), 10000);
                                  } catch (error) {
                                    console.error('Error opening PDF file:', error);
                                    toast.error('Failed to open PDF file. Please try again.');
                                  }
                                }
                              }}
                              title="Click to open in new tab"
                            >
                              <iframe
                                src={resumePreview}
                                className="w-full h-full scale-[0.25] origin-top-left"
                                style={{ transform: 'scale(0.25)', transformOrigin: 'top left', width: '400%', height: '400%' }}
                                title="Resume Preview"
                              />
                              <div className="absolute top-0 left-0 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded-br">
                                PDF
                              </div>
                              {/* Hover overlay */}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center pointer-events-none">
                                <div className="bg-white bg-opacity-90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                  <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div 
                              className="relative w-16 h-20 rounded-lg flex items-center justify-center cursor-pointer hover:shadow-md transition-all duration-200 group"
                                style={{ backgroundColor: '#EE7D63' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (resumeFile) {
                                  try {
                                    const fileUrl = URL.createObjectURL(resumeFile);
                                    const newWindow = window.open(fileUrl, '_blank');
                                    if (!newWindow) {
                                      toast.error('Please allow popups to view the file');
                                    }
                                    setTimeout(() => URL.revokeObjectURL(fileUrl), 10000);
                                  } catch (error) {
                                      console.error('Error opening file:', error);
                                      toast.error('Failed to open file. Please try again.');
                                  }
                                }
                              }}
                              title="Click to open in new tab"
                            >
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                                <div className="absolute top-0 left-0 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded-br">
                                  DOC
                                </div>
                                {/* Hover overlay */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center pointer-events-none">
                                  <div className="bg-white bg-opacity-90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                    <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-green-600 mb-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-sm font-medium">Resume uploaded</span>
                            </div>
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {resumeFile.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {((resumeFile.size || 0) / 1024 / 1024).toFixed(2)} MB • 
                              Ready for analysis
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setResumeFile(null);
                            setResumePreview(null);
                          }}
                          className="text-xs px-2 py-1"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Show upload prompt when no file
                    <div className="text-center">
                      <div className="p-4 rounded-full bg-gray-100 mb-4 inline-block">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <p className={`text-sm sm:text-base mb-2 font-medium transition-colors ${
                        isDragOverResume ? 'text-blue-600' : 'text-gray-600'
                      }`}>
                        {isDragOverResume ? 'Drop resume here' : 'Click to upload or drag and drop resume'}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500 mb-4">
                        PDF, DOC, DOCX (max 2MB)
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs sm:text-sm"
                      >
                        Choose File
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Job Description Upload */}
          <Card className="shadow-lg border-0 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <CardHeader className="bg-gray-100 px-4 sm:px-6 py-4 sm:py-5">
              <CardTitle className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: '#EE7D63' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                  </svg>
                </div>
                <span>Job Description</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-4">
                  {/* Text Input Field with Upload Button or Preview */}
                  <div className="space-y-3">
                   {jobDescriptionPreview ? (
                     // Show preview only when parsing failed
                     <div 
                       className="border-2 border-dashed border-gray-300 rounded-xl p-4 sm:p-6 hover:border-blue-400 hover:bg-blue-50/50 transition-all duration-300 cursor-pointer group min-h-[240px] flex items-center justify-center"
                       onClick={() => jobInputRef.current?.click()}
                     >
                      <input
                        ref={jobInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleJobDescriptionUpload}
                        className="hidden"
                      />
                      
                       {/* Show compact preview when file parsing failed */}
                       <div className="flex items-center justify-center w-full">
                         <div className="flex items-center justify-between w-full max-w-md">
                           <div className="flex items-center gap-3 flex-1 min-w-0">
                             <div className="flex-shrink-0">
                               {jobDescriptionPreview === 'image-based-pdf' && jobDescriptionFile?.type === 'application/pdf' ? (
                                <div 
                                  className="relative w-16 h-20 border border-gray-200 rounded overflow-hidden cursor-pointer hover:border-blue-400 hover:shadow-md transition-all duration-200 group"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (jobDescriptionFile) {
                                      try {
                                        const fileUrl = URL.createObjectURL(jobDescriptionFile);
                                        console.log('Opening PDF file:', jobDescriptionFile.name, 'Type:', jobDescriptionFile.type, 'URL:', fileUrl);
                                        const newWindow = window.open(fileUrl, '_blank');
                                        if (!newWindow) {
                                          toast.error('Please allow popups to view the file');
                                        }
                                        // Clean up the object URL after a delay
                                        setTimeout(() => URL.revokeObjectURL(fileUrl), 10000);
                                      } catch (error) {
                                        console.error('Error opening PDF file:', error);
                                        toast.error('Failed to open PDF file. Please try again.');
                                      }
                                    }
                                   }}
                                   title="Click to open in new tab"
                                 >
                                   <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                     <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                     </svg>
                                   </div>
                                   <div className="absolute top-0 left-0 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded-br">
                                     PDF
                                   </div>
                                  {/* Hover overlay */}
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center pointer-events-none">
                                    <div className="bg-white bg-opacity-90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                      <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                      </svg>
                        </div>
                                  </div>
                                </div>
                               ) : jobDescriptionPreview === 'word-document' ? (
                                 <div 
                                   className="relative w-16 h-20 rounded-lg flex items-center justify-center cursor-pointer hover:shadow-md transition-all duration-200 group"
                                   style={{ backgroundColor: '#EE7D63' }}
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     if (jobDescriptionFile) {
                                       try {
                                         const fileUrl = URL.createObjectURL(jobDescriptionFile);
                                         const newWindow = window.open(fileUrl, '_blank');
                                         if (!newWindow) {
                                           toast.error('Please allow popups to view the file');
                                         }
                                         setTimeout(() => URL.revokeObjectURL(fileUrl), 10000);
                                       } catch (error) {
                                         console.error('Error opening file:', error);
                                         toast.error('Failed to open file. Please try again.');
                                       }
                                     }
                                   }}
                                   title="Click to open in new tab"
                                 >
                                   <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                   </svg>
                                   <div className="absolute top-0 left-0 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded-br">
                                     DOC
                                   </div>
                                   {/* Hover overlay */}
                                   <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center pointer-events-none">
                                     <div className="bg-white bg-opacity-90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                       <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                       </svg>
                                     </div>
                                   </div>
                                 </div>
                               ) : null}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                          {jobDescriptionFile?.name}
                        </p>
                               <p className="text-xs text-gray-500">
                                 {((jobDescriptionFile?.size || 0) / 1024 / 1024).toFixed(2)} MB • 
                                 {jobDescriptionPreview === 'image-based-pdf' ? 'Image-based PDF' : 
                                  jobDescriptionPreview === 'word-document' ? 'Complex Word document' : 'Ready for analysis'}
                        </p>
                            </div>
                          </div>
                           <div className="flex-shrink-0 flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                               onClick={(e) => {
                                 e.stopPropagation();
                                setJobDescriptionPreview(null);
                                setJobDescriptionFile(null);
                                setJobDescriptionText('');
                              }}
                               className="text-xs px-2 py-1"
                            >
                              Remove
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                               onClick={(e) => {
                                 e.stopPropagation();
                                setJobDescriptionPreview(null);
                                setJobDescriptionText('');
                                // Keep file but allow manual text entry
                              }}
                               className="text-xs px-2 py-1"
                            >
                              Enter Text Instead
                            </Button>
                           </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Show text input when there's no file preview
                    <div className="relative">
                      <textarea
                        value={jobDescriptionText}
                        onChange={(e) => setJobDescriptionText(e.target.value)}
                        placeholder="Upload PDF/DOC/DOCX file (max 2MB) using the upload icon, or paste text manually..."
                        className="w-full h-[240px] p-3 sm:p-4 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent resize-none text-sm sm:text-base transition-colors duration-200"
                        style={{ '--tw-ring-color': '#EE7D63' } as React.CSSProperties}
                      />
                    <button
                      onClick={() => jobInputRef.current?.click()}
                      disabled={isParsingJobDescription}
                      className="absolute top-2 right-2 p-2 text-gray-400 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ color: '#EE7D63' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#EE7D63'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
                      title={isParsingJobDescription ? "Parsing file..." : "Upload file to auto-populate"}
                    >
                      {isParsingJobDescription ? (
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      )}
                    </button>
                    
                      {/* Parsing Progress Indicator */}
                      {isParsingJobDescription && (
                        <div className="absolute bottom-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 border border-gray-200">
                          <div className="flex items-center gap-2 mb-1">
                            <svg className="animate-spin w-3 h-3 text-[#EE7D63]" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-xs font-medium text-gray-700">Parsing file...</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1">
                            <div 
                              className="bg-[#EE7D63] h-1 rounded-full transition-all duration-300 ease-out"
                              style={{ width: `${parsingProgress}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-500 mt-1 block">{Math.round(parsingProgress)}% Complete</span>
                        </div>
                      )}
                      <input
                        ref={jobInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        title="Upload PDF, DOC, or DOCX file (max 2MB)"
                        onChange={handleJobDescriptionUpload}
                        className="hidden"
                      />
                    </div>
                  )}
                  
                  {!jobDescriptionPreview && jobDescriptionText.length > 0 && (
                  <div className="flex justify-between items-center">
                    <p className="text-xs sm:text-sm text-gray-500">
                        {jobDescriptionText.length} characters entered
                    </p>
                    </div>
                    )}
                  </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Analysis Button */}
       {!isAnalyzing && <div className="text-center mb-6 lg:mb-8">
            <Button 
              onClick={handleRunAnalysis}
              disabled={isAnalyzing}
              className="text-white px-8 py-3 rounded-lg text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              style={{ backgroundColor: '#EE7D63' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#D96A4F'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EE7D63'}
            >
              {analysisResult ? (
                'Run Again'
              ) : (
                'Run ATS Analysis'
              )}
            </Button>
        </div>}
            
        {/* Analysis Progress */}
            {isAnalyzing && (
          <Card className="shadow-lg border-0 rounded-2xl overflow-hidden mb-8">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-[#EE9C7F]  to-[#F2B5A0] mb-4">
                    <svg className="animate-spin w-8 h-8 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Running ATS Analysis</h3>
                  <p className="text-gray-600 mb-6">Analyzing your resume against the job description...</p>
                  </div>
                
                {/* Enhanced Progress Bar */}
                <div className="max-w-md mx-auto">
                  <div className="relative bg-gray-200 rounded-full h-3 overflow-hidden">
                    {/* Background gradient */}
                    <div className="absolute inset-0 bg-gradient-to-r from-[#EE9C7F] via-[#EE7D63] to-[#F2B5A0] opacity-20"></div>

                    
                    {/* Progress fill */}
                    <div 
                      className="relative h-full bg-gradient-to-r from-[#EE9C7F]  to-[#F2B5A0] rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${parsingProgress}%` }}
                    >
                      {/* Shimmer effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                </div>
                    
                    {/* Bouncing dots */}
                    <div className="absolute top-1/2 transform -translate-y-1/2 flex space-x-1" style={{ left: `${Math.min(parsingProgress + 5, 85)}%` }}>
                      <div className="w-1 h-1 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-1 h-1 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-1 h-1 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
          </div>
                  
                  <div className="flex justify-between items-center mt-3">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm font-bold text-gray-900">{Math.round(parsingProgress)}%</span>
        </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results Section */}
        {analysisResult && (
          <div className="bg-white rounded-lg shadow-lg border p-6">
            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Analysis Results</h2>
              <div className="text-4xl font-bold" style={{ color: '#EE7D63' }}>
                {analysisResult.data?.overallScore !== undefined ? `${analysisResult.data.overallScore}/10` : 'N/A'}
              </div>
              <p className="text-gray-600">Overall Score</p>
            </div>

            {/* Scores */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {analysisResult.data?.keywordMatch !== undefined && (
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold mb-1" style={{ color: '#EE7D63' }}>{analysisResult.data.keywordMatch}/10</div>
                  <div className="text-sm text-gray-600">Keyword Match</div>
                </div>
              )}
              {analysisResult.data?.experienceMatch !== undefined && (
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold mb-1" style={{ color: '#EE7D63' }}>{analysisResult.data.experienceMatch}/10</div>
                  <div className="text-sm text-gray-600">Experience Match</div>
                </div>
              )}
              {analysisResult.data?.experienceMatch !== undefined && (
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold mb-1" style={{ color: '#EE7D63' }}>{analysisResult.data.formattingScore}/10</div>
                  <div className="text-sm text-gray-600">Formatting Score</div>
                </div>
              )}
            </div>

            {/* Analysis Text */}
            <div className="space-y-4">
              {analysisResult.data?.skillsMatch && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Skills Analysis</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{analysisResult.data.skillsMatch}</p>
                </div>
              )}

              {analysisResult.data?.educationMatch && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Education Analysis</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{analysisResult.data.educationMatch}</p>
                </div>
              )}

              {analysisResult.keyFindings && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Key Findings</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{analysisResult.keyFindings}</p>
                </div>
              )}

              {analysisResult.recommendations && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Recommendations</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{analysisResult.recommendations}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AtsChecker;
